import { Col } from "react-bootstrap";
import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import { CarFormPart } from "../commons/carFormPart/carFormPart";
import { CarManageFormPart } from "../commons/carManageFormPart/carManageFormPart";
import { CarTireSetsFormPart } from "../commons/carTireSetsFormPart/carTireSetsFormPart";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import { CarTireSetModel } from "../../domain/models/carTireSetModel";
import {
  createCar,
  deleteCar,
  getCar,
  updateCar,
} from "../../infrastructure/slices/carMutateSlice";
import { useNavigate } from "react-router-dom";
import { removeItem } from "../../../general/infrastructure/slices/crudTableSlice/crudTableSlice";
import { carFormSchema } from "../../domain/schemas/carSchema";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { toast } from "sonner";
import { EntityLogs } from "../../../logs/modules/features/EntityLogs";
import { useMemo } from "react";
import { CustomFilters } from "../../../general/crudGraphql/domain/models/graphqlModel";
import { CarModel } from "../../domain/models/carModel";
import swal from "sweetalert";

export const CarMutateForm = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { t } = useTranslation("common");

  const { hasRole } = useAuthorization();

  const { car } = useAppSelector((state) => state.carMutate);

  const carForm = useForm({
    defaultValues: car === undefined ? { status: true } : car,
    validatorAdapter: zodValidator(),
    validators: {
      onSubmit: carFormSchema,
    },
  });

  const carTireSetsForm = useForm({
    defaultValues: {
      tireSets: car && car.tireSets ? car.tireSets : ([] as CarTireSetModel[]),
    },
    validatorAdapter: zodValidator(),
  });

  const onSubmit = () => {
    Promise.all([
      carForm.handleSubmit(),
      carTireSetsForm.handleSubmit(),
    ]).finally(() => {
      if (!carForm.state.isValid || !carTireSetsForm.state.isValid) {
        toast.error(t("Please fix the errors in the form."));
      }
      if (
        carForm.state.isValid &&
        carTireSetsForm.state.isValid &&
        carForm.state.canSubmit &&
        carTireSetsForm.state.canSubmit
      ) {
        let carValue = { ...carForm.state.values };
        const tireSetsValue = carTireSetsForm.state.values;

        if (hasRole("dealer-employee")) {
          delete carValue.comment;
          tireSetsValue.tireSets.forEach((set) => {
            delete set.comment;
          });
        }

        if (!carValue.id) {
          tireSetsValue.tireSets.forEach((set) => {
            delete set.id;
            set?.tires?.forEach((tire) => {
              delete tire.id;
            });
          });
        }

        carValue = { ...carValue, tireSets: tireSetsValue.tireSets };

        if (!(hasRole("super-admin") || hasRole("dbh-manager"))) {
          carValue.freestock_tire = undefined;
        }

        if (carValue.id === undefined) {
          void dispatch(createCar(carValue)).then((result) => {
            if (createCar.fulfilled.match(result)) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              navigate(`/car/${result.payload.id}/edit`);
            }
          });
        } else {
          void dispatch(updateCar(carValue)).then((result) => {
            if (updateCar.fulfilled.match(result)) {
              void dispatch(getCar(carValue.id?.toString() || "")).then(
                (result) => {
                  if (getCar.fulfilled.match(result)) {
                    carTireSetsForm.setFieldValue(
                      "tireSets",
                      (result.payload as CarModel).tireSets ||
                        ([] as CarTireSetModel[]),
                    );
                  }
                },
              );
            }
          });
        }
      }
    });
  };

  const onDelete = () => {
    const carValue = carForm.state.values;

    void swal({
      title: t(`crud.cars.delete.confirm`),
      buttons: {
        cancel: {
          visible: true,
          className: "btn btn-light btn-sm",
        },
        confirm: {
          visible: true,
          className: "btn btn-primary btn-sm",
        },
      },
    }).then((willDelete) => {
      if (willDelete) {
        if (carValue.id !== undefined) {
          navigate(`/tire-sets`);
          void dispatch(deleteCar(carValue.id)).then((result) => {
            if (deleteCar.fulfilled.match(result)) {
              carValue.tireSets?.forEach((tireset) => {
                dispatch(removeItem(tireset.id));
              });
            }
          });
        }
      }
    });
  };

  const customFilters: CustomFilters = useMemo(() => {
    return {
      where: {
        type: "SearchByRootActivityInput",
        value: {
          anyOf: [
            {
              allOf: [
                {
                  field: {
                    subject_type: {
                      equal: "car",
                    },
                  },
                },
                {
                  field: {
                    subject_id: {
                      equal: car?.id?.toString(),
                    },
                  },
                },
              ],
            },
            ...(car?.tireSets?.map((tireSet) => ({
              allOf: [
                {
                  field: {
                    subject_type: {
                      equal: "tireset",
                    },
                  },
                },
                {
                  field: {
                    subject_id: {
                      equal: tireSet.id?.toString(),
                    },
                  },
                },
              ],
            })) || []),
            ...(car?.tireSets?.flatMap((tireSet) =>
              tireSet.tires?.map((tire) => {
                return {
                  allOf: [
                    {
                      field: {
                        subject_type: {
                          equal: "tire",
                        },
                      },
                    },
                    {
                      field: {
                        subject_id: {
                          equal: tire.id?.toString(),
                        },
                      },
                    },
                  ],
                };
              }),
            ) || []),
          ],
        },
      },
      __typename: "custom",
    };
  }, [car?.id, car?.tireSets]);

  return (
    <>
      <Content md="6">
        <ContentHeader title={t("Car")} className="mb-3" />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore*/}
        <CarFormPart form={carForm} />
      </Content>
      <div className="col-md-6">
        <Content>
          <ContentHeader title={t("crud.manage.title")} className="mb-3" />
          <CarManageFormPart
            form={carForm}
            onSubmit={onSubmit}
            onDelete={onDelete}
          />
        </Content>
        <EntityLogs entity="car" filters={customFilters} />
      </div>
      <Col>
        <CarTireSetsFormPart
          form={carTireSetsForm}
          car={car}
          licensePlate={carForm.state.values.licencePlate}
          isFreestock={carForm.state.values.freestock_tire}
        />
      </Col>
    </>
  );
};
